import React, {useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet'
import {toast} from 'react-toastify'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {AuthSignHeader} from 'i18n/AuthSignHeader'
import {LocalLink} from 'i18n/LocalLink'
import {useI18n} from 'i18n/I18nContext'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import overlay_img from 'images/loginb2.webp'
import {routes} from 'routes/routes'
import {useQueryParams} from 'utils/lib/url'

/**
 * Landing page for the organization email-verification flow.
 * Mirror of {@see attendee/Auth/VerifyEmail.tsx} but scoped to
 * {@see OrganizationAuthProvider} and the /organization/login back-link.
 */
export function VerifyEmail() {
  const {t, locale} = useI18n()
  const navigate = useLocalizedNavigate()
  const params = useQueryParams()
  const {saveAccessToken, refreshUser, resendVerificationEmail} =
    useOrganizationAuth()

  const status = (params.status as string | undefined) ?? undefined
  const token = (params.token as string | undefined) ?? undefined
  const initialEmail = useMemo(
    () => (params.email as string | undefined) ?? '',
    [params.email],
  )

  const [email, setEmail] = useState<string>(initialEmail)
  const [isResending, setResending] = useState(false)

  useEffect(() => {
    if (status === 'ok' && token) {
      saveAccessToken(token)
      refreshUser().then((ok) => {
        if (!ok) {
          toast.error(t('auth.verifyEmail.errorToast'))
          navigate(routes.organization.login)
          return
        }
        toast.success(t('auth.verifyEmail.successToast'))
        navigate(routes.organization.list)
      })
    }
  }, [status, token, saveAccessToken, refreshUser, navigate, t])

  const onResend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setResending(true)
    const ok = await resendVerificationEmail(email)
    if (ok) {
      toast.success(t('auth.verifyEmail.resendSuccess'))
    }
    setResending(false)
  }

  const isInvalid = status === 'invalid'
  const isProcessing = status === 'ok'

  return (
    <>
      <Helmet>
        <title>{t('auth.verifyEmail.title')}</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <main className={`body-area bd-theme-light`}>
        <section className="signin__area">
          <div className="sign__main-wrapper">
            <div className="sign__left">
              <AuthSignHeader
                logo={
                  <div className="sign__logo">
                    <LocalLink to={routes.index}>
                      <img
                        style={{width: '150px', height: 'auto'}}
                        className="logo-black"
                        src={process.env.PUBLIC_URL + '/logo-dark.svg'}
                        alt={t('logo.alt')}
                      />
                    </LocalLink>
                  </div>
                }
                navLinks={
                  <div className="sign__link custome_link">
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.login}
                    >
                      {t('common.login')}
                    </LocalLink>
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.register}
                    >
                      {t('common.register')}
                    </LocalLink>
                  </div>
                }
              />
              <div className="sign__center-wrapper text-center mt-80">
                <div className="sign__title-wrapper mb-40">
                  <h3 className="sign__title">
                    {isInvalid
                      ? t('auth.verifyEmail.invalidTitle')
                      : isProcessing
                        ? t('auth.verifyEmail.processingTitle')
                        : t('auth.verifyEmail.pendingTitle')}
                  </h3>
                  <p>
                    {isInvalid
                      ? t('auth.verifyEmail.invalidBody')
                      : isProcessing
                        ? t('auth.verifyEmail.processingBody')
                        : t('auth.verifyEmail.pendingBody').replace(
                            '{email}',
                            initialEmail,
                          )}
                  </p>
                </div>

                {!isProcessing && (
                  <form
                    className="sign__input-form text-center"
                    onSubmit={onResend}
                    key={locale}
                  >
                    <div className="sign__input mb-32">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.signupEmailPlaceholder')}
                        required
                      />
                      <span>
                        <i className="flaticon-email"></i>
                      </span>
                    </div>
                    <div className="sing__button mb-20">
                      <button
                        disabled={isResending || !email}
                        className={
                          isResending || !email
                            ? 'disableBtnStyle w-100 mb-20'
                            : 'input__btn w-100 mb-20'
                        }
                        type="submit"
                      >
                        {t('auth.verifyEmail.resendButton')}
                      </button>
                    </div>
                  </form>
                )}

                <div className="if__account mt-80">
                  <p>
                    {t('auth.verifyEmail.backToLogin')}{' '}
                    <LocalLink to={routes.organization.login}>
                      {t('common.login')}
                    </LocalLink>
                  </p>
                </div>
              </div>
            </div>
            <div className="sign__right">
              <div
                className="sign__input-thumb include__bg w-img"
                style={{
                  backgroundImage: `url(${overlay_img})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                }}
              ></div>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
